import { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import Config from '../../config';
import { ImageEmbedded } from '../image/image-embedded';
import { TitleV2 } from '../titlev2/titlev2.component';
import TypographyV2 from '../typographyv2/typographyv2';

import { QuoteItemProps } from './quote-item.config';

const QUOTE_ICONS = {
  en: `${Config.DAM_IMAGES_PATH}quote-icons/opening-quote_en.svg`,
  ja: `${Config.DAM_IMAGES_PATH}quote-icons/opening-quote_ja.svg`,
  ko: `${Config.DAM_IMAGES_PATH}quote-icons/opening-quote_ko.svg`,
  de: `${Config.DAM_IMAGES_PATH}quote-icons/opening-quote_de.svg`,
  es: `${Config.DAM_IMAGES_PATH}quote-icons/opening-quote_es.svg`,
  fr: `${Config.DAM_IMAGES_PATH}quote-icons/opening-quote_fr.svg`,
  it: `${Config.DAM_IMAGES_PATH}quote-icons/opening-quote_it.svg`,
  pt_br: `${Config.DAM_IMAGES_PATH}quote-icons/opening-quote_pt_br.svg`,
};

const QuoteItem = ({
  additionalClasses,
  alignment,
  appliedCssClassNames,
  cqPath,
  image,
  layout,
  logo,
  quote,
  quoteSourceName,
  quoteSourceTitle,
  showHangingPunctuation,
  showQuoteIcon,
}: QuoteItemProps) => {
  const { i18n } = useTranslation();
  const [quoteIcon, setQuoteIcon] = useState(QUOTE_ICONS.en);
  const isCenterAligned = useMemo(() => alignment === 'center', [alignment]);
  const isCard = useMemo(() => layout === 'card', [layout]);
  const defaultBodyStyle = useMemo(() => (isCard ? 'body-1' : 'body-display'), [isCard]);

  useEffect(() => {
    setQuoteIcon(QUOTE_ICONS[i18n.language as keyof typeof QUOTE_ICONS] ?? QUOTE_ICONS.en);
  }, [i18n.language]);

  const quoteClass = useMemo(() => {
    if (appliedCssClassNames) {
      const regExp = /\b\w*body-\w+\b/;
      const matches = regExp.exec(appliedCssClassNames);
      return matches && matches.length > 0 ? matches[0] : defaultBodyStyle;
    }

    return defaultBodyStyle;
  }, [appliedCssClassNames, defaultBodyStyle]);

  const quoteText = useMemo(() => (showHangingPunctuation ? `“${quote}` : quote), [quote, showHangingPunctuation]);

  return (
    <div
      data-testid="snowflake-quote-item"
      className={classNames('snowflake-quote-item', additionalClasses, { 'snowflake-quote-item-card': isCard })}
    >
      {image && (
        <div className="snowflake-quote-item-image-container" data-testid="snowflake-quote-item-image">
          <ImageEmbedded {...image} baseCssClass="snowflake-quote-item-image" />
        </div>
      )}
      <div
        className={classNames('snowflake-quote-item-inner', {
          'snowflake-quote-item-inner-center': isCenterAligned,
          'snowflake-quote-item-inner-card': isCard,
          'snowflake-quote-item-inner-card-with-image': isCard && image,
        })}
        data-testid="snowflake-quote-item-inner"
      >
        {showQuoteIcon && (
          <div className="snowflake-quote-item-quote-icon" data-testid="snowflake-quote-item-quote-icon">
            <ImageEmbedded src={quoteIcon} alt="Quote Icon" lazyEnabled />
          </div>
        )}
        <TypographyV2
          className={classNames('snowflake-quote-item-quote-text', {
            'snowflake-quote-item-quote-text-center': isCenterAligned,
            'snowflake-quote-item-quote-text-with-punctuation': showHangingPunctuation,
          })}
          variant="paragraph"
          variantClassName={quoteClass}
        >
          {quoteText}
        </TypographyV2>
        {(logo || quoteSourceName || quoteSourceTitle) && (
          <div
            className={classNames('snowflake-quote-item-citation-container', {
              'snowflake-quote-item-citation-container-center': isCenterAligned,
              'snowflake-quote-item-citation-container-card': isCard,
            })}
          >
            {logo && (
              <div
                className="snowflake-quote-item-citation-logo snowflake-logo"
                data-testid="snowflake-quote-item-citation-logo"
              >
                <ImageEmbedded {...logo} baseCssClass="cmp-image" />
              </div>
            )}
            {(quoteSourceName || quoteSourceTitle) && (
              <div className="snowflake-quote-item-citation">
                {quoteSourceName && (
                  <div
                    className={classNames('snowflake-quote-item-citation-name', { center: isCenterAligned })}
                    data-testid="snowflake-quote-item-citation-name"
                  >
                    <TitleV2 cqPath={cqPath} isInEditor={false} lines={[quoteSourceName]} type="heading5" />
                  </div>
                )}
                {quoteSourceTitle && (
                  <div
                    className={classNames('snowflake-quote-item-citation-title', {
                      'snowflake-quote-item-citation-title-center': isCenterAligned,
                    })}
                    data-testid="snowflake-quote-item-citation-title"
                  >
                    <TypographyV2 variant="" variantClassName="body-2">
                      {quoteSourceTitle}
                    </TypographyV2>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteItem;
